/**
 * The **demo shell** — the app shell running on fixture data, with no session, no api and no
 * database (parity card `tasks/parity/P-002-fixture-board-without-shell.md`).
 *
 * `/__ui/board` used to paint two grey rectangles where the top bar and the sidebar belong, so
 * the board region landed at the reference coordinates without needing a backend. The user
 * reported that as a defect: a screen fixture has to be the *real* screen, chrome included, or
 * it proves nothing about the screen. So the harness now mounts the **same** `<AppShell/>` the
 * product mounts, and the three shell queries resolve against this context instead of tRPC.
 *
 * Nothing here is reachable from a product route: `useDemoShell()` returns `null` unless a
 * `<DemoShellProvider>` is above it, and only `/__ui/*` mounts one.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { BoardId } from '@elizmonday/shared';
import type { ShellCapabilities } from './bootstrap';
import { FIXTURE_BOARD_ID, FIXTURE_BOARD_NAME, FIXTURE_VIEW_ID } from '../board-view/data/fixture';
import type { SidebarPayload } from './tree';

/** The workspace and folder the fixture board is presented in. Ids are fixture-only. */
export const DEMO_WORKSPACE_ID = '900000000001';
export const DEMO_WORKSPACE_NAME = 'Sandbox';
export const DEMO_FOLDER_ID = '900000000002';
/** A board inside `DEMO_FOLDER_ID`, so the fixture tree has both depths (P-037). */
export const DEMO_NESTED_BOARD_ID = '900000000003';

export interface DemoBoardSummary {
  id: string;
  name: string;
  kind: string;
  state: string;
  workspaceId: string | null;
  itemTerminology: string | null;
  description: string | null;
  views: { id: string; name: string; type: string | null; isDefault: boolean; position: string }[];
}

export interface DemoShellData {
  bootstrap: DemoBootstrap;
  sidebar: SidebarPayload;
  board: DemoBoardSummary;
}

export interface DemoBootstrap {
  me: {
    id: string;
    name: string | null;
    email: string | null;
    role: string;
    photoUrl: string | null;
    theme: string;
  };
  account: { name: string | null; logoUrl: string | null; logoAssetId: string | null; homePage: 'Default' };
  capabilities: ShellCapabilities;
  preferences: Record<string, unknown>;
  workspaces: {
    recent: { id: string; name: string; kind: string }[];
    total: number;
    /** T-9h: the account's default workspace, where a cold start lands. */
    defaultId: string | null;
  };
  unread: { notifications: number; inbox: number };
  degraded: boolean;
}

const DemoShellContext = createContext<DemoShellData | null>(null);

export function useDemoShell(): DemoShellData | null {
  return useContext(DemoShellContext);
}

/** The fixture board id the demo shell routes to, so `/__ui/board` deep-links work. */
export const DEMO_BOARD_ID: BoardId = FIXTURE_BOARD_ID;

export function buildDemoShellData(): DemoShellData {
  return {
    bootstrap: {
      me: {
        id: '900000000010',
        name: 'Fixture User',
        email: 'fixture@elizmonday.local',
        role: 'admin',
        photoUrl: null,
        theme: 'light',
      },
      account: { name: 'Eli Z Monday', logoUrl: null, logoAssetId: null, homePage: 'Default' },
      // An admin's capability set, so the fixture screen shows the chrome the product shows.
      capabilities: {
        workspaceSwitcher: true,
        workspaceTree: true,
        sharedWithMeOnly: false,
        canCreateBoard: true,
        canCreateFolder: true,
        canCreateWorkspace: true,
        canCreateDashboard: true,
        favorites: true,
        myWork: true,
        inbox: true,
        notifications: true,
        search: 'account',
        inviteMembers: true,
        administration: true,
        developersPage: true,
        canCreateApiToken: true,
        teams: true,
        trash: true,
        archive: true,
        importData: true,
      },
      // The shell's own preference store owns the merge; the fixture only needs the shape.
      preferences: {},
      workspaces: {
        recent: [{ id: DEMO_WORKSPACE_ID, name: DEMO_WORKSPACE_NAME, kind: 'open' }],
        total: 1,
        defaultId: DEMO_WORKSPACE_ID,
      },
      unread: { notifications: 0, inbox: 0 },
      degraded: false,
    },
    sidebar: {
      workspace: { id: DEMO_WORKSPACE_ID, name: DEMO_WORKSPACE_NAME, kind: 'open' },
      folders: [
        {
          id: DEMO_FOLDER_ID,
          name: 'Column types',
          parentFolderId: null,
          color: null,
          position: 'a0000',
        },
      ],
      boards: [
        {
          id: String(FIXTURE_BOARD_ID),
          name: FIXTURE_BOARD_NAME,
          kind: 'public',
          state: 'active',
          type: 'board',
          // At the workspace root: `/__ui/board` carries no `:boardId`, so the tree has no
          // selected entity to reveal the path to and a nested board would stay hidden.
          folderId: null,
          position: 'a0001',
          isFavorite: true,
        },
        {
          /**
           * P-037's fixture: a board **inside** the folder, next to the root board above, so
           * `verify-chrome-local.mjs` can compare all three columns — the folder chevron, a
           * root board's icon (same column) and a child board's icon (one 22 px step in).
           */
          id: DEMO_NESTED_BOARD_ID,
          name: 'Nested board',
          kind: 'public',
          state: 'active',
          type: 'board',
          folderId: DEMO_FOLDER_ID,
          position: 'a0002',
          isFavorite: false,
        },
      ],
      docs: [],
      dashboards: [],
    },
    board: {
      id: String(FIXTURE_BOARD_ID),
      name: FIXTURE_BOARD_NAME,
      kind: 'public',
      state: 'active',
      workspaceId: DEMO_WORKSPACE_ID,
      itemTerminology: 'item',
      description: null,
      views: [
        { id: String(FIXTURE_VIEW_ID), name: 'Main Table', type: 'TableBoardView', isDefault: true, position: 'a0000' },
      ],
    },
  };
}

export function DemoShellProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => buildDemoShellData(), []);
  return <DemoShellContext.Provider value={value}>{children}</DemoShellContext.Provider>;
}
